import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Clock,
  Copy,
  Webhook,
  Activity,
  Pencil,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { testIntegration } from "@/lib/api";

const STATUS_STYLES = {
  connected: { label: "Connected", bg: "rgba(22,101,52,0.10)", color: "#166534", Icon: CheckCircle2 },
  configured: { label: "Configured · Untested", bg: "rgba(212,175,55,0.18)", color: "#7A5E12", Icon: Clock },
  missing: { label: "Missing keys", bg: "rgba(155,28,28,0.08)", color: "#9B1C1C", Icon: AlertCircle },
  error: { label: "Error", bg: "rgba(155,28,28,0.08)", color: "#9B1C1C", Icon: AlertTriangle },
};

function fmt(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function IntegrationCard({ service, reauthToken, onEdit, onChanged }) {
  const [testing, setTesting] = useState(false);
  const status = STATUS_STYLES[service.status] || STATUS_STYLES.missing;
  const { Icon } = status;

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testIntegration(service.key, reauthToken);
      if (res.ok) toast.success(`${service.name}: ${res.message}`);
      else toast.error(`${service.name}: ${res.message}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = async () => {
    if (!service.webhook_url) return;
    try {
      await navigator.clipboard.writeText(service.webhook_url);
      toast.success("Webhook URL copied.");
    } catch {
      toast.error("Could not copy. Select the URL manually.");
    }
  };

  return (
    <div
      className="rounded-xl border bg-white p-5 flex flex-col gap-3.5"
      style={{ borderColor: "#E2E8F0" }}
      data-testid={`integration-card-${service.key}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-semibold" style={{ color: "#0B2545" }} data-testid={`integration-name-${service.key}`}>
            {service.name}
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{service.description}</div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium shrink-0"
          style={{ background: status.bg, color: status.color }}
          data-testid={`integration-status-${service.key}`}
        >
          <Icon className="h-3 w-3" />
          {status.label}
        </span>
      </div>

      {/* Keys */}
      <div className="space-y-1.5">
        {service.keys.map((k) => (
          <div key={k.name} className="flex items-center justify-between gap-3 text-xs">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" style={{ color: "#0B2545" }}>{k.label}</div>
              <div className="font-mono text-[11px] truncate" style={{ color: "#64748B" }} data-testid={`integration-key-${k.name}`}>
                {k.is_set ? (k.secret ? (k.masked || "(set)") : (k.masked || "(empty)")) : (
                  <span style={{ color: "#9B1C1C" }}>not set</span>
                )}
              </div>
            </div>
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              style={{
                background: k.is_set
                  ? (k.source === "database" ? "rgba(11,37,69,0.06)" : "rgba(212,175,55,0.18)")
                  : "rgba(155,28,28,0.08)",
                color: k.is_set
                  ? (k.source === "database" ? "#0B2545" : "#7A5E12")
                  : "#9B1C1C",
              }}
            >
              {k.is_set ? k.source : "missing"}
            </span>
          </div>
        ))}
      </div>

      {/* Webhook */}
      {service.webhook_url && (
        <div
          className="rounded-lg border px-3 py-2.5 flex items-center gap-2"
          style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
        >
          <Webhook className="h-3.5 w-3.5 shrink-0" style={{ color: "#7A5E12" }} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: "#64748B" }}>Webhook endpoint</div>
            <div className="text-xs font-mono truncate" style={{ color: "#0B2545" }} title={service.webhook_url}>
              {service.webhook_url}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={copyWebhook}
            className="h-7 px-2 text-[11px] shrink-0"
            data-testid={`copy-webhook-${service.key}`}
          >
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
        </div>
      )}

      {/* Last test */}
      {service.last_test_at && (
        <div className="text-[11px]" style={{ color: service.last_test_ok ? "#166534" : "#9B1C1C" }} data-testid={`last-test-${service.key}`}>
          <span className="opacity-80">Last test {fmt(service.last_test_at)}:</span>{" "}
          <span className="font-medium">{service.last_test_message || (service.last_test_ok ? "ok" : "failed")}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1 mt-auto">
        <Button
          size="sm"
          variant="outline"
          onClick={handleTest}
          disabled={testing}
          className="h-8 text-xs"
          data-testid={`test-${service.key}`}
        >
          {testing ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Activity className="h-3 w-3 mr-1.5" />}
          Test connection
        </Button>
        <Button
          size="sm"
          onClick={() => onEdit(service)}
          className="h-8 text-xs ml-auto"
          style={{ background: "#0B2545", color: "#fff" }}
          data-testid={`edit-${service.key}`}
        >
          <Pencil className="h-3 w-3 mr-1.5" />
          Edit / Rotate keys
        </Button>
      </div>
    </div>
  );
}
